<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useCarinfoStore } from '@/stores/car-info'
import { useYMapCircle } from '@/composables/useYMapCircle.js'
import vinDecoder from '@/helpers/vinDecoder'
import newElement from '@/helpers/newElement'
import colorToHex from '@/helpers/colorToHex.js'

// Props
const props = defineProps({
    coords: {
        type: Array,
        required: true
    },
    area: {
        type: Object,
        required: false
    }
})

// Store
const carinfoStore = useCarinfoStore()

// Data
let map = null
const mapContainer = ref(null)
const mapCenter = ref([37.588144, 55.733842])
const mapZoom = ref(18)

// Computed
const carVin = computed(() => carinfoStore.carinfo.data.vin)

function updateMapInfo(data) {
    mapZoom.value = data.location?.zoom || 18

    localStorage.setItem('UTA_LOGS_MAP_ZOOM_LVL', mapZoom.value)
}

// Инициализация карты
async function initYMap() {

    if(props.coords) mapCenter.value = props.coords
    
    const {
        YMap, 
        YMapDefaultSchemeLayer, 
        YMapDefaultFeaturesLayer, 
        YMapControls, 
        YMapScaleControl, 
        YMapMarker, 
        YMapListener
    } = ymaps3

    const {YMapZoomControl} = await ymaps3.import('@yandex/ymaps3-default-ui-theme')

    // установка зума
    if (localStorage.getItem('UTA_LOGS_MAP_ZOOM_LVL')) {
        mapZoom.value = JSON.parse(localStorage.getItem('UTA_LOGS_MAP_ZOOM_LVL'))
    }
    
    map = new YMap(mapContainer.value, {
            location: {
                center: mapCenter.value,
                zoom: mapZoom.value
            }
        },
        [
            new YMapDefaultSchemeLayer({}),     // Слой для тайлов
            new YMapDefaultFeaturesLayer({})    // Слой для геообъектов на карте
        ]
    )

    // Масштаб
    const bLeftControl = new YMapControls({position: 'bottom left'}, [new YMapScaleControl({})])
    map.addChild(bLeftControl)
    
    // Кнопки управления зумом
    const rightControl = new YMapControls({position: 'right'})
    rightControl.addChild(new YMapZoomControl({}))
    map.addChild(rightControl)

    const mapListener = new YMapListener({
        layer: 'any',
        onActionEnd: updateMapInfo
    })

    const vinDecode = vinDecoder(carVin.value)
    const modelName = vinDecode.name
    
    // Маркер автомобиля
    const markerCarElement = newElement('div', 'marker-car')
    const markerCarIconElement = newElement('div', ['marker-car__icon', `marker-car__icon--${modelName}`])
    markerCarElement.appendChild(markerCarIconElement)

    const markerCar = new YMapMarker({
        coordinates: props.coords,
        margin: [15, 26, 0, 0], 
        zIndex: 3 
    }, markerCarElement)

    map.addChild(markerCar)
    map.addChild(mapListener)
}

// Рисование зоны, где произошло событие
function renderArea(area) {
    // console.log(area)

    const { YMapFeature } = ymaps3

    let layer = null
    const style = {
        stroke: [
            {
                color: colorToHex(area.options?.strokeColor) || '#3388ff',
                width: area.options?.strokeWidth || 0
            }
        ]
    }

    // const areaDesc = {
    //     name: area.name,
    //     uuid: area.uuid,
    //     options: area.options
    // }

    if(area.geometry["polygon"]) {
        const polygonCoords = area.geometry.polygon.array.map(latLng => [latLng.lon, latLng.lat])
        style.fill = colorToHex(area.options?.fillColor) || '#3388ff'
        
        layer = new YMapFeature({
            geometry: {
                type: 'Polygon',
                coordinates: [polygonCoords]
            },
            id: area.uuid,
            // draggable: true,
            // onClick: layerClickHandler,
            style: style
        })

        // layer.area = areaDesc
        layer.area.type = "polygon"
    }

    if(area.geometry["line"]) {
        const lineCoords = area.geometry.line.array.map(latLng => [latLng.lon, latLng.lat])

        layer = new YMapFeature({
            geometry: {
                type: 'LineString',
                coordinates: lineCoords
            },
            id: area.uuid,
            // onClick: layerClickHandler,
            style: style
        })

        // layer.area = areaDesc
        layer.area.type = "line"
    }

    if(area.geometry["circle"]) {
        const center = [area.geometry.circle.point.lon, area.geometry.circle.point.lat]
        const radius = area.geometry.circle.radius
        
        style.simplificationRate = 0
        style.fill = colorToHex(area.options?.fillColor) || '#3388ff'

        const options = {
            editable: false,
            uuid: area.uuid,
            style
        }

        const { yMapCircle } = useYMapCircle(center, radius, options)

        layer = yMapCircle.value
        // layer.area = areaDesc
        layer.area.type = "circle"
    }

    if(layer) map.addChild(layer)
}

onMounted(async()  => {

    // инициализировать карту
    await initYMap()

    if(props.area) renderArea(props.area)
})

onBeforeUnmount(() => {
    
    // Уничтожить карту 
    if(ymaps3 && map) {
        map.destroy()
        map = null
    }
})
</script>

<template>
    <div ref="mapContainer" class="uta-logs-map"></div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/logs-map.scss';
</style>